import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, AlertCircle } from 'lucide-react'
import { useStore } from '../store/useStore'
import RichTextEditor from './RichTextEditor'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

const MAX_TITLE_LENGTH = 500

interface AddTaskModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDate?: Date | null
  defaultProjectId?: string | null
}

export default function AddTaskModal({ isOpen, onClose, defaultDate, defaultProjectId }: AddTaskModalProps) {
  const { t } = useTranslation()
  const { addTodo, projects, selectedProjectId } = useStore()
  const { soundEnabled } = useFeedbackSettings()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [projectId, setProjectId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [titleError, setTitleError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showProjectWarning, setShowProjectWarning] = useState(false)
  const startDateInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('')
      setDescription('')
      setTitleError('')
      setStartDate('')
      setSelectedTime('')

      // Set default project
      if (defaultProjectId) {
        setProjectId(defaultProjectId)
      } else if (selectedProjectId && selectedProjectId !== 'all') {
        setProjectId(selectedProjectId)
      } else {
        setProjectId('')
      }

      // Set default date
      if (defaultDate) {
        setSelectedDate(format(defaultDate, 'yyyy-MM-dd'))
      } else {
        setSelectedDate('')
      }

      // Focus title input
      setTimeout(() => {
        titleInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, defaultDate, defaultProjectId, selectedProjectId])

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setTitle(newTitle)

    if (newTitle.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${newTitle.length}文字）`)
    } else {
      setTitleError('')
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value)
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(e.target.value)
  }

  const getStartDate = () => {
    if (!startDate) return null
    return `${startDate}T00:00:00`
  }

  const getDueDate = () => {
    if (!selectedDate) return null
    if (selectedTime) {
      return `${selectedDate}T${selectedTime}:00`
    }
    return `${selectedDate}T00:00:00`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || isSubmitting) return

    if (title.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${title.length}文字）`)
      return
    }

    setIsSubmitting(true)
    try {
      initializeFeedback()

      const targetProjectId = projectId || (selectedProjectId !== 'all' ? selectedProjectId : null)
      if (!targetProjectId) {
        setShowProjectWarning(true)
        setIsSubmitting(false)
        return
      }

      const taskStartDate = getStartDate()
      const dueDate = getDueDate()

      await addTodo({
        title: title.trim(),
        description: description.trim() || null,
        priority: 3,
        status: 'pending',
        project_id: targetProjectId,
        start_date: taskStartDate || null,  // Already in local ISO format
        due_date: dueDate || null,  // Already in local ISO format
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small',
        assigned_to_user_id: null,
        assignment_status: 'unassigned',
        progress_percentage: 0
      })

      triggerFeedback('create', { soundEnabled })
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const clearStartDate = () => {
    setStartDate('')
  }

  const clearDate = () => {
    setSelectedDate('')
    setSelectedTime('')
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:w-[480px] sm:rounded-lg rounded-t-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-indigo-600">
          <h3 className="text-lg font-semibold text-white">新しいタスクを追加</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-indigo-500 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              タスク名 <span className="text-red-500">*</span>
            </label>
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="タスク名を入力..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                titleError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
              }`}
              required
            />
            {titleError && (
              <p className="text-red-600 text-xs mt-1">{titleError}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              詳細（任意）
            </label>
            <RichTextEditor
              content={description}
              onChange={setDescription}
              placeholder="詳細を入力..."
            />
          </div>

          {/* Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              プロジェクト <span className="text-red-500">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">
                {selectedProjectId && selectedProjectId !== 'all'
                  ? projects.find(p => p.id === selectedProjectId)?.name || 'プロジェクトを選択'
                  : 'プロジェクトを選択'}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date and Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              期間（任意）
            </label>
            <div className="flex items-center gap-2">
              {/* Start Date */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">開始日</label>
                <div className="flex items-center gap-1">
                  <div
                    className="relative cursor-pointer flex-1"
                    onClick={() => startDateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={startDateInputRef}
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={selectedDate || undefined}
                      className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-10 px-2 text-sm border rounded-lg bg-gray-50 flex items-center">
                      {startDate || 'YYYY-MM-DD'}
                    </div>
                  </div>
                  {startDate && (
                    <button
                      type="button"
                      onClick={clearStartDate}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="開始日をクリア"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              <span className="text-gray-400 mt-5">〜</span>

              {/* Due Date */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">期限</label>
                <div className="flex items-center gap-1">
                  <div
                    className="relative cursor-pointer flex-1"
                    onClick={() => dateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      min={startDate || undefined}
                      className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-10 px-2 text-sm border rounded-lg bg-gray-50 flex items-center">
                      {selectedDate || 'YYYY-MM-DD'}
                    </div>
                  </div>
                  {selectedDate && (
                    <button
                      type="button"
                      onClick={clearDate}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="期限をクリア"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Time (shown when due date is selected) */}
            {selectedDate && (
              <div className="mt-2">
                <label className="block text-xs text-gray-500 mb-1">
                  期限時刻（任意）
                </label>
                <div
                  className="relative cursor-pointer w-32"
                  onClick={() => timeInputRef.current?.showPicker?.()}
                >
                  <input
                    ref={timeInputRef}
                    type="time"
                    value={selectedTime}
                    onChange={handleTimeChange}
                    className="w-full h-10 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                    style={{ colorScheme: 'light' }}
                  />
                  <div className="h-10 px-3 text-sm border rounded-lg bg-gray-50 flex items-center">
                    {selectedTime || '--:--'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '追加中...' : '追加'}
            </button>
          </div>
        </form>

        {/* Project Warning Dialog */}
        {showProjectWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl">
              <div className="flex items-start mb-4">
                <AlertCircle className="h-6 w-6 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('project.selectProjectTitle')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('project.selectProjectRequired')}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowProjectWarning(false)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
